import { DownloadOutlined } from '@ant-design/icons'
import type { SelectChangeEvent } from '@mui/material'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import MultiPeriodSelector from 'components/MultiPeriodSelector'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import { postWithTokenWithStatus } from 'hooks/http'
import type { FC } from 'react'
import { useMemo } from 'react'
import type { Period } from 'types/periods'
import type { RewardPackage } from 'types/rewards'
import type { UserProfile } from 'types/user-profile'
import type { FormType, SnackbarState } from './types'
import { DOWNLOAD_FORM_TYPE_OPTIONS, FORM_TYPE_MAP, REQUIRED_SELECTORS } from './types'
import { useGlobalState } from 'contexts/GlobalStateContext'

interface DownloadSectionProps {
  profile: UserProfile
  token: string
  rootOrganizationId: string
  formType: FormType | ''
  selectedOrganizations: string[]
  selectedPeriods: string[]
  rootPeriodId: string
  selectedRootPeriodId: string
  selectedPackageId: string
  periods: Period[]
  rewardPackages: RewardPackage[]
  isLoadingPeriods: boolean
  isLoadingPackages: boolean
  onFormTypeChange: (formType: FormType | '') => void
  onOrganizationChange: (selected: string[]) => void
  onPeriodChange: (selected: string[]) => void
  onRootPeriodChange: (periodId: string) => void
  onPackageChange: (packageId: string) => void
  onSnackbarChange: (snackbar: SnackbarState) => void
}

const DownloadSection: FC<DownloadSectionProps> = ({
  profile,
  token,
  rootOrganizationId,
  formType,
  selectedOrganizations,
  selectedPeriods,
  rootPeriodId,
  selectedRootPeriodId,
  selectedPackageId,
  periods,
  rewardPackages,
  isLoadingPeriods,
  isLoadingPackages,
  onFormTypeChange,
  onOrganizationChange,
  onPeriodChange,
  onRootPeriodChange,
  onPackageChange,
  onSnackbarChange,
}) => {
  const theme = useTheme()
  const { checkSubscriptionTierFeature } = useGlobalState()
  const requiredSelectors = useMemo(() => {
    return formType ? REQUIRED_SELECTORS[formType] : []
  }, [formType])

  // Helper function to recursively find a period by ID
  const findPeriodById = (periods: Period[], id: string): Period | undefined => {
    for (const period of periods) {
      if (period.id === id) {
        return period
      }
      if (period.children) {
        const found = findPeriodById(period.children, id)
        if (found) return found
      }
    }
    return undefined
  }

  // Helper function to filter out closed periods
  const filterClosedPeriods = (periodIds: string[]): string[] => {
    // Only filter for actuals forms
    if (formType !== 'import-team-actuals' && formType !== 'import-employee-actuals') {
      return periodIds
    }

    return periodIds.filter((id) => {
      const period = findPeriodById(periods, id)
      return period && !period.isClosed
    })
  }

  const handleFormTypeChange = (event: SelectChangeEvent) => {
    onFormTypeChange(event.target.value as FormType)
  }

  const handleSubmit = async () => {
    if (!formType) return

    try {
      const requestBody: any = {
        formType: FORM_TYPE_MAP[formType],
        rootOrganizationId,
      }

      if (requiredSelectors.includes('organization')) {
        requestBody.organizationIds = selectedOrganizations
      }

      if (requiredSelectors.includes('period')) {
        // Filter out closed periods before sending to API
        const filteredPeriodIds = filterClosedPeriods(selectedPeriods)

        // If all periods were filtered out, show an error
        if (filteredPeriodIds.length === 0 && selectedPeriods.length > 0) {
          onSnackbarChange({
            open: true,
            message: 'All selected periods are closed. Please select open periods.',
            severity: 'error',
          })
          return
        }

        requestBody.periodIds = filteredPeriodIds
        requestBody.rootPeriodId = rootPeriodId
      }

      if (requiredSelectors.includes('rootPeriod')) {
        requestBody.rootPeriodId = selectedRootPeriodId
        requestBody.periodIds = [selectedRootPeriodId]
      }

      if (requiredSelectors.includes('package')) {
        requestBody.packageId = selectedPackageId
      }

      const { data, status } = await postWithTokenWithStatus('forms/download', token, requestBody)

      if (status === 200 && data.downloadUrl) {
        const link = document.createElement('a')
        link.href = data.downloadUrl
        link.setAttribute('download', '')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        onSnackbarChange({
          open: true,
          message: 'Download started. Please check your downloads folder.',
          severity: 'success',
        })
      } else {
        onSnackbarChange({
          open: true,
          message: data.error || 'Unexpected response from server',
          severity: 'error',
        })
      }
    } catch (error) {
      console.error('Error downloading form:', error)
      onSnackbarChange({
        open: true,
        message: 'Error downloading form',
        severity: 'error',
      })
    }
  }

  const isSubmitDisabled =
    !formType ||
    requiredSelectors.some((selector) => {
      if (selector === 'organization') return selectedOrganizations.length === 0
      if (selector === 'period') return selectedPeriods.length === 0
      if (selector === 'rootPeriod') return !selectedRootPeriodId
      if (selector === 'package') return !selectedPackageId
      return false
    }) ||
    rootOrganizationId === ''

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DownloadOutlined />
            <Typography variant="h6">Download Form for Import</Typography>
          </Box>
        }
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid size={12}>
            <FormControl variant="outlined" sx={{ width: '100%' }}>
              <InputLabel shrink>Form Type</InputLabel>
              <Select
                value={formType}
                variant="outlined"
                label="Form Type"
                onChange={handleFormTypeChange}
                displayEmpty
                sx={{ borderRadius: '21px' }}
              >
                {DOWNLOAD_FORM_TYPE_OPTIONS.filter((option) => {
                  if (checkSubscriptionTierFeature('REWARDS.ALL')) {
                    return ![
                      'import-components',
                      'import-targets',
                      'import-team-actuals',
                      'import-employee-actuals',
                    ].includes(option.value)
                  } else {
                    return !['import-rewards', 'import-user-rewards'].includes(option.value)
                  }
                }).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {requiredSelectors.includes('organization') && (
            <Grid size={12}>
              <MultiOrganizationSelector
                userProfile={profile}
                selectedOrganizationIds={selectedOrganizations}
                handleChange={onOrganizationChange}
                labelBackgroundColor="inherit"
              />
            </Grid>
          )}

          {requiredSelectors.includes('period') && (
            <Grid size={12}>
              <MultiPeriodSelector
                key="period-selector"
                periods={periods}
                selectedPeriods={selectedPeriods}
                handleChange={onPeriodChange}
                disabled={isLoadingPeriods || selectedOrganizations.length === 0 || periods.length === 0}
                label="Select Periods"
                onlySingleRootPeriodSelection
                labelBackgroundColor="inherit"
                disableClosed={formType === 'import-team-actuals' || formType === 'import-employee-actuals'}
              />
            </Grid>
          )}

          {requiredSelectors.includes('rootPeriod') && (
            <Grid size={12}>
              <SinglePeriodSelector
                periods={periods}
                value={selectedRootPeriodId}
                onChange={onRootPeriodChange}
                rootPeriodOnly
                label="Select Period"
                disabled={isLoadingPeriods || periods.length === 0}
                pill
                sortOrder="asc"
                labelBackgroundColor="inherit"
              />
            </Grid>
          )}

          {requiredSelectors.includes('package') && (
            <Grid size={12}>
              <FormControl variant="outlined" sx={{ width: '100%' }}>
                <InputLabel
                  shrink
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  Select Reward Package
                </InputLabel>
                <Select
                  value={selectedPackageId}
                  variant="outlined"
                  label="Select Reward Package"
                  onChange={(e) => onPackageChange(e.target.value)}
                  displayEmpty
                  disabled={isLoadingPackages || rewardPackages.length === 0}
                  sx={{ borderRadius: '21px' }}
                >
                  <MenuItem value="" disabled>
                    Select a reward package
                  </MenuItem>
                  {rewardPackages.map((pkg) => (
                    <MenuItem key={pkg.id} value={pkg.id || ''}>
                      {pkg.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </CardContent>

      <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isSubmitDisabled}>
          Download Form
        </Button>
      </Box>
    </Card>
  )
}

export default DownloadSection
